import { ReactNode } from 'react';

import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { Icon } from '@components/index';
import InfoIconTooltip from '@components/info-icon-tooltip/InfoIconTooltip';
import { FormDataType, TermsList } from '@types';

import { hasFrequencyChanged } from './helper';

import { PaymentCollectionType } from '../../constants';

type GroupChangesCardProps = {
  formData: FormDataType;
  initialFormData: FormDataType;
};

const getOrdinalSuffix = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

const getMonthlyLabel = (activationDate: string): string => {
  if (activationDate === 'first_day') return 'First day of month';
  if (activationDate === 'last_day') return 'Last day of month';
  return `${activationDate}${getOrdinalSuffix(
    Number(activationDate)
  )} day of month`;
};

const getRecursLabel = (formData: FormDataType): string => {
  const { paymentCollectionType, activationDate, isCustomTerm } = formData;

  if (isCustomTerm) return 'By term : Advanced';

  // Every 3 months on 20th day of month day

  switch (paymentCollectionType) {
    case PaymentCollectionType.MONTHLY:
      if (
        formData?.intervalFrequency &&
        Number(formData?.intervalFrequency) === 1
      )
        return `Monthly: ${getMonthlyLabel(activationDate)}`;
      return `Every ${
        formData?.intervalFrequency
      } months on ${activationDate}${getOrdinalSuffix(
        Number(activationDate)
      )} day`;
    case PaymentCollectionType.WEEKLY:
      return `Weekly: ${activationDate}`;
    case PaymentCollectionType.ONCE:
      return 'Once';
    case PaymentCollectionType.BY_TERM:
      return 'By term';
    case PaymentCollectionType.SESSION_BASED:
      return 'Session based';
    default:
      return '';
  }
};

const formatDisplayDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

const normalizeDate = (dateStr: string): number =>
  new Date(new Date(dateStr).setHours(0, 0, 0, 0)).getTime();

const hasTermAmountsChanged = (
  termsList?: TermsList[],
  initialTermsList?: TermsList[]
): boolean => {
  if (!termsList || !initialTermsList) return false;
  if (termsList.length !== initialTermsList.length) return true;
  return termsList.some(
    (term, i) => term.dueAmount !== initialTermsList[i].dueAmount
  );
};

const hasTermDueDatesChanged = (
  termsList?: TermsList[],
  initialTermsList?: TermsList[]
): boolean => {
  if (!termsList || !initialTermsList) return false;
  if (termsList.length !== initialTermsList.length) return true;
  return termsList.some(
    (term, i) =>
      normalizeDate(term.dueDate) !== normalizeDate(initialTermsList[i].dueDate)
  );
};

const TermSplitTooltipContent = ({
  termsList,
  initialTermsList
}: {
  termsList: TermsList[];
  initialTermsList: TermsList[];
}) => {
  const isTermAmountChanged = (term: TermsList): string | number | null => {
    const initial = initialTermsList.find((t) => t.id === term.id);
    if (term.dueAmount !== initial?.dueAmount) {
      return Number(initial?.dueAmount || 0);
    }
    return null;
  };

  return (
    <div className="flex min-w-44 flex-col gap-1.5 rounded-lg bg-cofeeNeutral1 p-3">
      {termsList.map((term) => (
        <div key={term.id} className="flex items-center justify-between gap-8">
          <span className="text-sm text-cofeeNeutral7">{term.term_label}</span>
          <div className="flex items-center gap-1">
            {isTermAmountChanged(term) ? (
              <CurrencyAmount
                value={isTermAmountChanged(term)}
                className="text-sm text-cofeeNeutral7 line-through"
              />
            ) : null}
            <CurrencyAmount
              value={term.dueAmount}
              className="text-sm text-cofeeNeutral10"
            />
          </div>
        </div>
      ))}
      <div className="mt-1 border-t border-dashed border-cofeeNeutral4 pt-1.5">
        <div className="flex items-center justify-between gap-8">
          <span className="text-sm font-medium text-cofeeNeutral10">
            Total amount
          </span>
          <CurrencyAmount
            value={termsList.reduce(
              (sum, t) => sum + Number(t.dueAmount || 0),
              0
            )}
            className="text-sm font-medium text-cofeeNeutral10"
          />
        </div>
      </div>
    </div>
  );
};

const DueDateChangesTooltipContent = ({
  termsList,
  initialTermsList
}: {
  termsList: TermsList[];
  initialTermsList: TermsList[];
}) => {
  const changedTerms = termsList.filter((term, i) => {
    const initial = initialTermsList[i];
    return (
      initial && normalizeDate(term.dueDate) !== normalizeDate(initial.dueDate)
    );
  });

  return (
    <div className="flex min-w-44 flex-col gap-1.5 p-2">
      <span className="text-sm font-medium text-cofeeNeutral10">
        Due date changes
      </span>
      {changedTerms.map((term) => {
        const initial = initialTermsList.find((t) => t.id === term.id);
        return (
          <div
            key={term.id}
            className="flex w-full items-center justify-between gap-8"
          >
            <span className="text-sm text-cofeeNeutral7">
              {term.term_label}
            </span>
            <span className="flex items-center gap-0.5 text-center text-sm text-cofeeNeutral10">
              <span className="w-20">
                {initial ? formatDisplayDate(initial.dueDate) : ''}
              </span>
              <span className="my-auto pb-1 text-cofeeNeutral10">→</span>
              <span className="w-20">{formatDisplayDate(term.dueDate)}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

const GroupChangesCard = ({
  formData,
  initialFormData
}: GroupChangesCardProps) => {
  const { isCustomTerm, termsList } = formData;
  const initialTermsList = initialFormData.termsList;

  const totalAmountChanged = formData.amount !== initialFormData.amount;
  const termAmountsChanged =
    isCustomTerm && hasTermAmountsChanged(termsList, initialTermsList);
  const amountChanged = totalAmountChanged || termAmountsChanged;

  const frequencyChanged = hasFrequencyChanged(formData, initialFormData);
  const startDateChanged = formData.startDate !== initialFormData.startDate;
  const endDateChanged = formData.endDate !== initialFormData.endDate;
  const termDueDatesChanged =
    isCustomTerm && hasTermDueDatesChanged(termsList, initialTermsList);
  const sessionsPerCycleChanged =
    formData.sessionsPerCycle !== initialFormData.sessionsPerCycle;

  const isOnceGroup =
    formData.paymentCollectionType === PaymentCollectionType.ONCE;
  const onceDueDateChanged =
    isOnceGroup && formData.activationDate !== initialFormData.activationDate;

  const cards: {
    label: string;
    value: ReactNode;
    description?: string;
  }[] = [];

  if (amountChanged) {
    const showTermSplit = isCustomTerm && termsList && termsList.length > 0;
    cards.push({
      label: 'Amount',
      value: showTermSplit ? (
        <div className="flex items-center gap-1">
          <CurrencyAmount
            value={formData.amount}
            className="text-base font-medium text-cofeeNeutral10"
          />
          <InfoIconTooltip
            id="group-term-split"
            variant="light"
            classname="!shadow-tooltipShadowNew"
            noArrow={false}
          >
            <TermSplitTooltipContent
              termsList={termsList}
              initialTermsList={initialTermsList || []}
            />
          </InfoIconTooltip>
        </div>
      ) : (
        <CurrencyAmount
          value={formData.amount}
          className="text-base font-medium text-cofeeNeutral10"
        />
      )
    });
  }

  if (frequencyChanged) {
    cards.push({
      label: 'Recurs',
      value: termDueDatesChanged ? (
        <div className="flex items-center gap-1">
          <span>{getRecursLabel(formData)}</span>
          <InfoIconTooltip
            id="group-due-date-changes"
            variant="light"
            classname="!shadow-tooltipShadowNew"
            place="bottom"
            noArrow={false}
          >
            <DueDateChangesTooltipContent
              termsList={termsList || []}
              initialTermsList={initialTermsList || []}
            />
          </InfoIconTooltip>
        </div>
      ) : (
        getRecursLabel(formData)
      )
    });
  }

  if (
    !frequencyChanged &&
    termDueDatesChanged &&
    termsList &&
    initialTermsList
  ) {
    cards.push({
      label: 'Recurs',
      value: (
        <div className="flex items-center gap-1">
          <span>{getRecursLabel(formData)}</span>
          <InfoIconTooltip
            id="group-due-date-changes"
            variant="light"
            classname="!shadow-tooltipShadowNew"
            place="bottom"
            noArrow={false}
          >
            <DueDateChangesTooltipContent
              termsList={termsList}
              initialTermsList={initialTermsList}
            />
          </InfoIconTooltip>
        </div>
      )
    });
  }

  if (startDateChanged && !isOnceGroup) {
    cards.push({
      label: 'Group activation date',
      value: formatDisplayDate(formData.startDate || '')
    });
  }

  if (endDateChanged && !isOnceGroup) {
    cards.push({
      label: 'Group deactivation date',
      value: formatDisplayDate(formData.endDate || '')
    });
  }

  if (onceDueDateChanged) {
    cards.push({
      label: 'Due date',
      value: formatDisplayDate(formData.activationDate || ''),
      description: amountChanged
        ? 'Date change will be applied to all members'
        : undefined
    });
  }

  if (sessionsPerCycleChanged && formData.sessionsPerCycle) {
    cards.push({
      label: 'Billing period',
      value: String(formData.sessionsPerCycle)
    });
  }

  if (cards.length === 0) return null;

  return (
    <div className="mb-5">
      <p className="mb-2 text-base font-semibold text-cofeeNeutral10">
        Group changes
      </p>
      <div className="flex gap-2 rounded-lg">
        {cards.map((card) => (
          <div
            key={card.label}
            className="flex w-full min-w-32 flex-col items-center justify-center gap-1 rounded-md bg-cofeeBlue2 p-3"
          >
            <p className="text-sm font-medium text-cofeeNeutral8">
              {card.label}
            </p>
            <div className="text-base font-medium text-cofeeNeutral10">
              {card.value}
            </div>
            {card.description && (
              <div className="flex items-center gap-2">
                <Icon name="info" size="12" className="rotate-180" />
                <p className="text-xs text-cofeeBlue9">{card.description}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GroupChangesCard;
